import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  Label,
  ResponsiveContainer,
} from "recharts";
import { Tooltip, Typography } from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";

const formatTooltipValue = (value) => {
  const num = parseFloat(value);
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)} trillion`;
  }
  return `${num.toFixed(2)} billion`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "#1c1834",
          padding: "5px",
          border: "1px solid #ddd",
          borderRadius: "16px",
          color: "#fff",
        }}
      >
        <p>{`Year: ${label}`}</p>
        <p>{`Market Cap: $${formatTooltipValue(payload[0].value)}`}</p>
        <p>{`Revenue: $${formatTooltipValue(payload[1].value)}`}</p>
      </div>
    );
  }

  return null;
};

function MarketCapGraph({ stats }) {
  function extractValue(str) {
    console.log(typeof str, "str");
    const numericPart = parseFloat(str.match(/[\d\.]+/)[0]);

    const multiplier = str.includes("trillion") ? 1000 : 1;

    return numericPart * multiplier;
  }

  const data = [];
  const marketCapPast =
    stats["Market Cap and Market Growth"]["Past 5 years"][
      "Market Capitalization Growth"
    ];
  const revenuePast =
    stats["Market Cap and Market Growth"]["Past 5 years"]["Revenue Growth"];

  Object.keys(marketCapPast).forEach((year) => {
    data.push({
      year,
      "Past Market Cap": extractValue(marketCapPast[year]),
      "Past Revenue": extractValue(revenuePast[year]),
    });
  });

  const marketCapFuture =
    stats["Market Cap and Market Growth"]["Next 5 years"][
      "Market Capitalization Growth"
    ];

  const revenueFuture =
    stats["Market Cap and Market Growth"]["Next 5 years"]["Revenue Growth"];

  Object.keys(marketCapFuture).forEach((year) => {
    data.push({
      year,
      "Current Market Cap": extractValue(marketCapFuture[year]),
      "Current Revenue": extractValue(revenueFuture[year]),
    });
  });

  function getUnits(data) {
    let units = [];

    const traverseAndExtractUnits = (obj) => {
      for (let key in obj) {
        if (typeof obj[key] === "object") {
          traverseAndExtractUnits(obj[key]);
        } else if (typeof obj[key] === "string") {
          let unit = obj[key].includes("trillion") ? "trillion" : "billion";
          units.push({ key: key, year: obj[key], unit: unit });
        }
      }
    };

    traverseAndExtractUnits(data);
    return units;
  }

  const unitsData = getUnits(stats);

  return (
    <div style={{ width: "100%", height: 300 }}>
      <Typography variant="h6" gutterBottom color={"#fff"}>
        Market Cap and Market Growth in $$$ Value
        <Tooltip
          title={
            <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
              <li>
                <Typography variant="subtitle1">
                  Market Capitalization Growth:
                </Typography>
                <Typography variant="body2">
                  This represents the increase in the total market value of the
                  digital service provider's outstanding shares over a specific
                  period. It is calculated by subtracting the market
                  capitalization of the previous year from the market
                  capitalization of the current year.
                </Typography>
              </li>
              <li>
                <Typography variant="subtitle1">Revenue Growth:</Typography>
                <Typography variant="body2">
                  This indicates the increase in revenue generated by the
                  digital service provider over a specific period. It is
                  calculated by subtracting the revenue of the previous year
                  from the revenue of the current year.
                </Typography>
              </li>
            </ul>
          }
        >
          <HelpIcon
            fontSize="small"
            style={{ marginLeft: 4, verticalAlign: "middle" }}
          />
        </Tooltip>
      </Typography>
      <Typography variant="body1" gutterBottom color={"grey"}>
        Past and Future 5 years
      </Typography>

      <ResponsiveContainer>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis>
            <Label
              value={`Valuation in ${unitsData[0]?.unit}`}
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: "middle" }}
              offset={4}
              dy={10}
            />
          </YAxis>
          <RTooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            type="monotone"
            dataKey="Past Market Cap"
            stroke="#6B41FC"
            activeDot={{ r: 8 }}
          />
          <Line type="monotone" dataKey="Past Revenue" stroke="#4DC369" />
          <Line
            type="monotone"
            dataKey="Current Market Cap"
            stroke="#F49958"
            activeDot={{ r: 8 }}
          />
          <Line type="monotone" dataKey="Current Revenue" stroke="#63BDFF" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default MarketCapGraph;
